import clsx from 'clsx'

export interface FilterChipOption<K extends string> {
  key: K
  label: string
  /** Shown beside the label when > 0. */
  count?: number
  /** A count that needs someone's attention (to approve, to review). */
  countNeedsAttention?: boolean
}

interface FilterChipsProps<K extends string> {
  options: FilterChipOption<K>[]
  value: K
  onChange: (key: K) => void
  /** Accessible name for the group, e.g. "Show". */
  label?: string
  className?: string
}

/**
 * The one way a list screen offers its views ("All", "Owing", "Vacant").
 * Single-select, each chip a real server filter. Chip radius, not pill —
 * see docs/BRAND_AND_DESIGN_SYSTEM.md §4.
 */
export function FilterChips<K extends string>({ options, value, onChange, label = 'Show', className }: FilterChipsProps<K>) {
  return (
    <div className={clsx('flex flex-wrap gap-2', className)} role="group" aria-label={label}>
      {options.map((option) => {
        const active = option.key === value
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.key)}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-chip text-body-sm font-medium border',
              'transition-colors duration-fast ease-standard focus-visible:outline-none focus-visible:shadow-focus',
              active
                ? 'bg-accent text-on-accent border-accent'
                : 'bg-surface text-ink-muted border-hairline hover:text-ink hover:bg-surface-alt',
            )}
          >
            {option.label}
            {option.count != null && option.count > 0 && (
              <span
                className={clsx(
                  'min-w-[1.25rem] px-1.5 rounded-chip text-caption tabular',
                  active
                    ? 'bg-on-accent/20'
                    : option.countNeedsAttention
                      ? 'bg-warning-soft text-warning'
                      : 'bg-surface-alt text-ink-muted',
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export default FilterChips
